import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Header() {
  const { t, lang, switchLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const homePath = `/${lang}`;
  const isOnHome = location.pathname === homePath || location.pathname === `${homePath}/`;

  const scrollTo = (id) => {
    setOpen(false);
    if (!isOnHome) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className="sticky top-0 z-40 w-full bg-white border-b border-slate-200"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to={homePath}
          className="flex items-center gap-3"
          data-testid="header-logo-link"
          onClick={() => setOpen(false)}
        >
          <span className="inline-flex h-9 w-9 items-center justify-center bg-[#0F172A] text-[#B48E5D] font-bold">
            M
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-xl text-[#0F172A] tracking-tight">
              {t("brand.name")}
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {t("brand.tagline")}
            </span>
          </span>
        </Link>

        <nav
          className="hidden md:flex items-center gap-8"
          aria-label="primary"
          data-testid="header-nav-desktop"
        >
          {isOnHome ? (
            <>
              <button
                type="button"
                onClick={() => scrollTo("how-it-works")}
                className="text-sm font-medium text-[#0F172A] hover:text-[#1E3A8A]"
                data-testid="nav-how-it-works"
              >
                {t("nav.how_it_works")}
              </button>
              <button
                type="button"
                onClick={() => scrollTo("opportunities")}
                className="text-sm font-medium text-[#0F172A] hover:text-[#1E3A8A]"
                data-testid="nav-opportunities"
              >
                {t("nav.opportunities")}
              </button>
              <button
                type="button"
                onClick={() => scrollTo("newsletter")}
                className="text-sm font-medium text-[#0F172A] hover:text-[#1E3A8A]"
                data-testid="nav-newsletter"
              >
                {t("nav.newsletter")}
              </button>
            </>
          ) : (
            <NavLink
              to={homePath}
              className="text-sm font-medium text-[#0F172A] hover:text-[#1E3A8A]"
              data-testid="nav-home"
            >
              {t("nav.home")}
            </NavLink>
          )}

          <div className="flex items-center gap-2 pl-6 border-l border-slate-200">
            <Globe className="h-4 w-4 text-slate-500" aria-hidden />
            <button
              type="button"
              onClick={() => switchLanguage("ar")}
              className={`text-sm font-semibold ${lang === "ar" ? "text-[#1E3A8A]" : "text-slate-500 hover:text-[#0F172A]"}`}
              data-testid="lang-switch-ar"
              aria-pressed={lang === "ar"}
            >
              ES
            </button>
            <span className="text-slate-300">/</span>
            <button
              type="button"
              onClick={() => switchLanguage("en")}
              className={`text-sm font-semibold ${lang === "en" ? "text-[#1E3A8A]" : "text-slate-500 hover:text-[#0F172A]"}`}
              data-testid="lang-switch-en"
              aria-pressed={lang === "en"}
            >
              EN
            </button>
          </div>
        </nav>

        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center h-11 w-11 border border-slate-200 text-[#0F172A]"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? t("nav.close_menu") : t("nav.open_menu")}
          data-testid="header-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-slate-200 bg-white"
          data-testid="header-nav-mobile"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {isOnHome ? (
              <>
                <button
                  onClick={() => scrollTo("how-it-works")}
                  className="text-left text-base font-medium text-[#0F172A]"
                  data-testid="nav-mobile-how-it-works"
                >
                  {t("nav.how_it_works")}
                </button>
                <button
                  onClick={() => scrollTo("opportunities")}
                  className="text-left text-base font-medium text-[#0F172A]"
                  data-testid="nav-mobile-opportunities"
                >
                  {t("nav.opportunities")}
                </button>
                <button
                  onClick={() => scrollTo("newsletter")}
                  className="text-left text-base font-medium text-[#0F172A]"
                  data-testid="nav-mobile-newsletter"
                >
                  {t("nav.newsletter")}
                </button>
              </>
            ) : (
              <Link
                to={homePath}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[#0F172A]"
                data-testid="nav-mobile-home"
              >
                {t("nav.home")}
              </Link>
            )}

            <div className="pt-4 border-t border-slate-200 flex items-center gap-3">
              <Globe className="h-4 w-4 text-slate-500" />
              <button
                type="button"
                onClick={() => {
                  switchLanguage("ar");
                  setOpen(false);
                }}
                className={`text-sm font-semibold ${lang === "ar" ? "text-[#1E3A8A]" : "text-slate-500"}`}
                data-testid="lang-mobile-ar"
              >
                {t("nav.language_es")}
              </button>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                onClick={() => {
                  switchLanguage("en");
                  setOpen(false);
                }}
                className={`text-sm font-semibold ${lang === "en" ? "text-[#1E3A8A]" : "text-slate-500"}`}
                data-testid="lang-mobile-en"
              >
                {t("nav.language_en")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
